// NOW, inside the ORB. There is no separate NOW screen: the orb that hangs
// from the compass IS the Today moment. This file draws what lives inside it:
// the title line, the five ORDER rows (all always visible; the row for the
// current time of day is open, the rest one line), and the four rim arrows
// (navigation lives in the edge tabs now).
//   O  ORACLE readout + a one-line "why off / how back"
//   R  NUMA — Charge · Load · Flow as three dots
//   D  KYRS top pick
//   E  the current Step (title, why, Done / Open) + its faculty
//   R  the evening session (CLIO by default) — it closes the day
// Only data the app already has is shown; where there is none, a quiet note.
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import type { GearId } from '../olmps/ascension';
import type { SelfState } from './useSelf';
import type { NowStep, NowPace } from './useCurrentStep';
import { GEAR_LABEL, GEAR_ORDER, SEAT_BY_ID } from './roster';
import { Medallion } from './ui';
import { DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

/** Which ORDER row is open for a given time of day. Morning, before the
 *  evening session: O → R → D in turn; daytime: E; evening: the last R. */
export function timeGear(now: Date): GearId {
  const h = now.getHours() + now.getMinutes() / 60;
  if (h >= 5 && h < 7) return 'orient';
  if (h >= 7 && h < 8) return 'regulate';
  if (h >= 8 && h < 10) return 'direct';
  if (h >= 10 && h < 18) return 'execute';
  return 'revise';
}

const PACE_LABEL = { fresh: 'Fresh', steady: 'Steady', strained: 'Strained', drained: 'Drained' } as const;
const ROW_IDS = ['o', 'r', 'd', 'e', 'r2'] as const;

export interface NowOrbProps {
  size: number;
  /** Selected compass spoke ("Health", …) and the stage title, e.g. "Today". */
  spokeLabel: string;
  sector: NowStep['sector'];
  stageLabel: string;
  rungState: string;
  steps: NowStep[];
  stepIdx: number;
  onStepIdx: (i: number) => void;
  pace: NowPace | null | undefined;
  self: SelfState;
  loading: boolean;
  now: Date;
  openGear: GearId;
  selectedGear: GearId;
  /** Tap a row: open it AND select its tool for the tool screen. */
  onPickRow: (gear: GearId) => void;
  why: string;
  onWhy: (text: string) => void;
  onTalk: (seatId: string) => void;
  onOpenStepTool: (step: NowStep) => void;
  onDone?: (step: NowStep) => void;
  onOpenTool: (toolId: string) => void;
  /** Open the morning (O·R·D) or evening (close the day) session. */
  onOpenSession?: (kind: 'morning' | 'night') => void;
  onSetDirection: () => void;
}

export function NowOrb(p: NowOrbProps) {
  const t = useV10();
  const styles = useStyles();
  const { size: S } = p;
  const step = p.steps[p.stepIdx] ?? p.steps[0] ?? null;
  const rowH = Math.max(21, Math.min(36, Math.round(S * 0.092)));
  const gridW = Math.round(S * 0.72);
  const gridH = Math.round(S * 0.62);
  const seatView = step ? p.self.byGear[step.gear] : undefined;
  const seat = seatView ? SEAT_BY_ID[seatView.seat.id] : step ? SEAT_BY_ID[step.tool] : undefined;
  const sectorColor = step?.sector ? SECTOR_COLOR[step.sector] : t.gold;
  const kyrsPick = p.steps.find((s) => s.source === 'kyrs') ?? p.steps.find((s) => s.gear === 'direct') ?? null;
  const evening = p.now.getHours() >= 18 || p.now.getHours() < 5;
  const paceTxt = p.pace ? PACE_LABEL[p.pace.state] : null;
  const col = (v: number, good: number, ok: number, invert = false) =>
    invert ? (v < good ? t.onPace : v < ok ? t.behind : t.lapsed) : v >= good ? t.onPace : v >= ok ? t.behind : t.lapsed;

  const title = [p.stageLabel, p.rungState, paceTxt].filter(Boolean).join(' · ');

  // One-line summary shown on a collapsed row.
  const summary = (g: GearId): { text: string; dots?: string[] } => {
    switch (g) {
      case 'orient':
        return { text: step?.trail.aim || step?.trail.endeavour || 'ORACLE — set a direction' };
      case 'regulate':
        return p.pace
          ? { text: `${paceTxt} · C${p.pace.charge.toFixed(1)}`, dots: [col(p.pace.charge, 7, 4), col(p.pace.load, 500, 700, true), col(p.pace.flow, 60, 30)] }
          : { text: 'NUMA — no reading yet' };
      case 'direct':
        return { text: kyrsPick?.title ?? 'KYRS — no pick yet' };
      case 'execute':
        return { text: step?.title ?? (p.loading ? 'Finding your place…' : 'Nothing pressing') };
      default:
        return { text: evening ? 'Evening session — close the day' : 'Evening session' };
    }
  };

  const body = (g: GearId) => {
    switch (g) {
      case 'orient':
        return (
          <View style={styles.bodyCol}>
            <Text style={styles.readout} numberOfLines={2}>
              {step
                ? [step.sector ? SECTOR_NAME[step.sector] : null, step.trail.endeavour, step.trail.aim].filter(Boolean).join(' › ') || 'No direction set yet'
                : 'No direction set yet'}
            </Text>
            <TextInput
              value={p.why}
              onChangeText={p.onWhy}
              placeholder="Why off? How back?"
              placeholderTextColor={t.ivoryFaint}
              style={styles.whyInput}
              maxLength={140}
            />
            {p.onOpenSession && !evening ? (
              <Pressable onPress={() => p.onOpenSession?.('morning')} style={styles.btnGhost}>
                <Text style={styles.btnGhostText}>Open morning session</Text>
              </Pressable>
            ) : null}
          </View>
        );
      case 'regulate':
        return p.pace ? (
          <View style={[styles.bodyCol, { justifyContent: 'center' }]}>
            <View style={styles.dotsRow}>
              {[
                { k: 'Charge', v: p.pace.charge.toFixed(1), c: col(p.pace.charge, 7, 4) },
                { k: 'Load', v: String(Math.round(p.pace.load)), c: col(p.pace.load, 500, 700, true) },
                { k: 'Flow', v: String(Math.round(p.pace.flow)), c: col(p.pace.flow, 60, 30) },
              ].map((d) => (
                <View key={d.k} style={styles.dotCell}>
                  <View style={[styles.bigDot, { backgroundColor: d.c, boxShadow: `0 0 10px ${d.c}` } as object]} />
                  <Text style={styles.dotVal}>{d.v}</Text>
                  <Text style={styles.dotKey}>{d.k}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.quiet}>NUMA has no reading for today yet.</Text>
        );
      case 'direct':
        return kyrsPick ? (
          <View style={styles.bodyCol}>
            <Text style={styles.stepTitle} numberOfLines={2}>
              {kyrsPick.title}
            </Text>
            <Text style={styles.readout}>{kyrsPick.when}</Text>
          </View>
        ) : (
          <Text style={styles.quiet}>KYRS has no pick yet — choose today's tasks.</Text>
        );
      case 'execute':
        if (!step) {
          return (
            <View style={styles.bodyCol}>
              <Text style={styles.quiet}>{p.loading ? 'Finding your place…' : 'Nothing is pressing right now.'}</Text>
              {!p.loading ? (
                <Pressable onPress={p.onSetDirection} style={styles.btnGhost}>
                  <Text style={styles.btnGhostText}>Set direction</Text>
                </Pressable>
              ) : null}
            </View>
          );
        }
        return (
          <View style={styles.bodyCol}>
            <View style={styles.stepHead}>
              {p.steps.length > 1 ? (
                <Pressable hitSlop={8} onPress={() => p.onStepIdx((p.stepIdx - 1 + p.steps.length) % p.steps.length)}>
                  <Text style={styles.pager}>‹</Text>
                </Pressable>
              ) : null}
              <Text style={[styles.stepTitle, { flex: 1, color: t.ivory }]} numberOfLines={2}>
                {step.title}
              </Text>
              {p.steps.length > 1 ? (
                <Pressable hitSlop={8} onPress={() => p.onStepIdx((p.stepIdx + 1) % p.steps.length)}>
                  <Text style={styles.pager}>›</Text>
                </Pressable>
              ) : null}
            </View>
            <Text style={styles.readout} numberOfLines={2}>
              {step.why}
            </Text>
            <View style={styles.btnRow}>
              {seat ? (
                <Pressable onPress={() => p.onTalk(seat.id)} style={styles.faculty} hitSlop={6}>
                  <Medallion id={seat.id} size={26} state="active" />
                  <Text style={styles.facultyText} numberOfLines={1}>
                    {seat.name}
                  </Text>
                </Pressable>
              ) : (
                <View style={{ flex: 1 }} />
              )}
              {p.onDone ? (
                <Pressable onPress={() => p.onDone?.(step)} style={styles.btnGold}>
                  <LinearGradient colors={[t.goldBright, t.gold]} style={[StyleSheet.absoluteFill, { borderRadius: 999 }]} pointerEvents="none" />
                  <Text style={styles.btnGoldText}>Done</Text>
                </Pressable>
              ) : null}
              <Pressable onPress={() => p.onOpenStepTool(step)} style={styles.btnGhost}>
                <Text style={styles.btnGhostText}>Open</Text>
              </Pressable>
            </View>
          </View>
        );
      default:
        return (
          <View style={styles.bodyCol}>
            <Text style={styles.readout}>
              {evening ? 'Time to revise: sweep what changed, then close the day.' : 'The evening session revises the day and closes it.'}
            </Text>
            <Pressable onPress={() => (p.onOpenSession ? p.onOpenSession('night') : p.onOpenTool('clio'))} style={styles.btnGhost}>
              <Text style={styles.btnGhostText}>{p.onOpenSession ? 'Open evening session' : 'Open CLIO'}</Text>
            </Pressable>
          </View>
        );
    }
  };

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <View style={[styles.inner, { paddingTop: Math.round(S * 0.115) }]} pointerEvents="box-none">
        <View style={styles.kickRow}>
          <View style={[styles.kickDot, { backgroundColor: sectorColor }]} />
          <Text style={[styles.kicker, { color: sectorColor }]} numberOfLines={1}>
            {p.spokeLabel || (step?.sector ? SECTOR_NAME[step.sector] : 'Today')}
          </Text>
        </View>
        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>
        <View style={[styles.grid, { width: gridW, height: gridH }]}>
          {GEAR_ORDER.map((g, i) => {
            const open = g === p.openGear;
            const sel = g === p.selectedGear;
            const sum = summary(g);
            return (
              <View key={ROW_IDS[i]} style={[styles.row, open ? { flex: 1 } : { height: rowH }, sel && styles.rowSel]}>
                <LinearGradient
                  colors={open ? (t.panelGradient as [string, string]) : [t.glass, t.glass]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={StyleSheet.absoluteFill}
                  pointerEvents="none"
                />
                <Pressable onPress={() => p.onPickRow(g)} style={[styles.rowHead, { height: rowH }]}>
                  <View style={[styles.letter, (open || sel) && styles.letterOn]}>
                    <Text style={[styles.letterText, (open || sel) && { color: t.onGold }]}>{GEAR_LABEL[g].letter}</Text>
                  </View>
                  {open ? (
                    <Text style={styles.rowOpenName} numberOfLines={1}>
                      {GEAR_LABEL[g].name}
                      <Text style={styles.rowQuestion}>  {GEAR_LABEL[g].question}</Text>
                    </Text>
                  ) : (
                    <Text style={styles.rowSum} numberOfLines={1}>
                      {sum.text}
                    </Text>
                  )}
                  {!open && sum.dots ? (
                    <View style={styles.miniDots}>
                      {sum.dots.map((c, k) => (
                        <View key={k} style={[styles.miniDot, { backgroundColor: c }]} />
                      ))}
                    </View>
                  ) : null}
                </Pressable>
                {open ? <View style={styles.rowBody}>{body(g)}</View> : null}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    inner: { flex: 1, alignItems: 'center' },
    kickRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    kickDot: { width: 7, height: 7, borderRadius: 4 },
    kicker: { fontSize: 10, fontWeight: '800', letterSpacing: 2.2, textTransform: 'uppercase' },
    title: { marginTop: 3, marginBottom: 7, maxWidth: '82%', fontSize: 10.5, fontFamily: DISPLAY_FONT, letterSpacing: 1.6, color: t.goldBright, textTransform: 'uppercase' },
    grid: { gap: 3 },
    row: { borderRadius: 9, overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine },
    rowSel: { borderColor: t.goldBright, boxShadow: `0 0 12px ${t.goldGlow}` } as object,
    rowHead: { flexDirection: 'row', alignItems: 'center', gap: 7, paddingRight: 8 },
    letter: { width: 26, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center', borderRightWidth: 1, borderRightColor: t.navyLine, backgroundColor: t.navyRaised },
    letterOn: { backgroundColor: t.gold, borderRightColor: t.goldBright },
    letterText: { fontFamily: DISPLAY_FONT, color: t.goldBright, fontSize: 14 },
    rowSum: { flex: 1, color: t.ivorySoft, fontSize: 11, fontWeight: '600' },
    rowOpenName: { flex: 1, color: t.goldBright, fontSize: 11.5, fontWeight: '800', letterSpacing: 1.2, textTransform: 'uppercase' },
    rowQuestion: { color: t.ivoryFaint, fontSize: 10, fontWeight: '600', letterSpacing: 0, textTransform: 'none' },
    miniDots: { flexDirection: 'row', gap: 3 },
    miniDot: { width: 7, height: 7, borderRadius: 4 },
    rowBody: { flex: 1, paddingHorizontal: 8, paddingBottom: 6 },
    bodyCol: { flex: 1, gap: 4, justifyContent: 'space-between' },
    readout: { color: t.ivorySoft, fontSize: 11, lineHeight: 14 },
    quiet: { color: t.ivoryFaint, fontSize: 11, fontStyle: 'italic', lineHeight: 15 },
    whyInput: { height: 24, borderRadius: 8, borderWidth: 1, borderColor: t.gold, backgroundColor: t.navy, color: t.ivory, fontSize: 11, paddingHorizontal: 8, paddingVertical: 0 },
    stepHead: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    stepTitle: { color: t.ivory, fontSize: 12.5, fontWeight: '800', lineHeight: 15 },
    pager: { color: t.goldBright, fontSize: 18, fontWeight: '700', paddingHorizontal: 2 },
    btnRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    faculty: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 5 },
    facultyText: { flex: 1, color: t.ivoryFaint, fontSize: 10.5, fontWeight: '700' },
    btnGold: { height: 24, paddingHorizontal: 14, borderRadius: 999, alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: `0 0 10px ${t.goldGlow}` } as object,
    btnGoldText: { color: t.onGold, fontSize: 11.5, fontWeight: '800' },
    btnGhost: { height: 24, paddingHorizontal: 12, borderRadius: 999, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.gold },
    btnGhostText: { color: t.goldBright, fontSize: 11.5, fontWeight: '800' },
    dotsRow: { flexDirection: 'row', justifyContent: 'space-around' },
    dotCell: { alignItems: 'center', gap: 2 },
    bigDot: { width: 20, height: 20, borderRadius: 10 },
    dotVal: { color: t.ivory, fontSize: 12, fontWeight: '800' },
    dotKey: { color: t.ivoryFaint, fontSize: 9.5, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
    arrow: { position: 'absolute', width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', zIndex: 6, overflow: 'hidden', borderWidth: 1, borderColor: t.goldBright, boxShadow: `0 0 10px ${t.goldGlow}` } as object,
    arrowDim: { opacity: 0.28, boxShadow: 'none' } as object,
    arrowText: { fontSize: 11, color: t.onGold, fontWeight: '800' },
  }),
);
